import React, {useState, useEffect, useRef} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import DatePicker from "react-datepicker";
import {
    formatDateTimeToString, formatDateTimeNoSecondsToString,
    formatDateToAmericanFormat,
    formatDateToString, UserPermission, statusTranslate, renderAutomotiveServiceOptions, CompanyPermission
} from "../../utils/utils";
import Config from "../../core/config";
import {updateAutomotiveServiceStatus} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import {getAuthHeader} from "../../utils/auth";
import {useNavigate} from "react-router-dom";
import 'react-datepicker/dist/react-datepicker.css';
import ModalDiv from "../../ModalDiv";
import {useReactToPrint} from "react-to-print";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import NewButton from '../common/NewButton';
import SearchButton from '../common/SearchButton';
import TableRowActions from '../common/TableRowActions';
import WhatsAppButton from '../../core/WhatsAppButton';
import {buildDefaultWhatsAppMessage} from '../../utils/whatsappUtils';
import Loading from "../../core/Loading";
import {loadingAtom} from "../../atoms";
import {usePermission} from "../../core/PermissionContext";
import PaymentMethodValueSelect from "../paymentMehod/PaymentMethodValueSelect";
import AutomotiveServiceViewer from "./AutomotiveServiceViewer";
import ListPageLayout from '../common/ListPageLayout';
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const formatMoneyBr = (value) =>
    Number(value ?? 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});

const statusStyle = (statusKey) => statusTranslate[statusKey] || statusTranslate.OPENING;

const AutomotiveServiceList = () => {
    const { hasUserPermission, loadingPermissions, hasCompanyPermission } = usePermission();
    const [services, setServices] = useState([]);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [status, setStatus] = useState('IN_PROGRESS');
    const [totalValue, setTotalValue] = useState(0);
    const navigate = useNavigate();
    const [serviceShowPrintDialogIndex, setServiceShowPrintDialogIndex] = useState(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Estados para controle do modal de pagamento
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [serviceToUpdate, setServiceToUpdate] = useState(null);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [showServiceViewer, setShowServiceViewer] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    const componentRef = useRef();

    const [loading, setLoading] = useRecoilState(loadingAtom);

    async function makeQuery() {
        try {
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/automotiveservices/queryByDateAndSearch?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${encodeURIComponent(status)}&searchTerm=${encodeURIComponent(searchTerm ?? '')}`;
            setLoading(true);
            const response = await makeRequest(url, 'GET');
            if (!Array.isArray(response)) {
                setServices([]);
                return;
            }
            const updatedResponse = response.map((service) => ({
                ...service,
                customerName: (service.customerName ?? '').toString().toUpperCase(),
            }));
            setServices(updatedResponse);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery();
    }, []);

    useEffect(() => {
        setTotalValue(services.reduce((acc, obj) => acc + obj.totalValue, 0));
    }, [services]);

    async function removeService(id) {
        try {
            setLoading(true);
            const url = apiUrl + '/api/automotiveservices/' + id;
            await makeRequest(url, 'DELETE');
            await makeQuery();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleQuery = async (e) => {
        await makeQuery();
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const [sortConfig, setSortConfig] = useState({key: 'deliveryDate', direction: 'ascending'});

    const [sortedServices, setSortedServices] = useState([]);

    useEffect(() => {
        setSortedServices([...services].sort((a, b) => {
            const aValue = sortConfig.key.includes('.') ? a[sortConfig.key.split('.')[0]][sortConfig.key.split('.')[1]] : a[sortConfig.key];
            const bValue = sortConfig.key.includes('.') ? b[sortConfig.key.split('.')[0]][b[sortConfig.key.split('.')[1]]] : b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        }));
    }, [services, sortConfig]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({key, direction});
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

    function printService(serviceIndex) {
        setServiceShowPrintDialogIndex(serviceIndex);
    }

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: '',
        pageStyle: `
    @media print {
        * {
            font-size: 12px !important;
        }
    }
    `,
    });

    const handleInputChange = (e) => {
        const string = e.target.value;
        setSearchTerm(string);
    };

    async function handleStatus(index, status) {
        // Se a permissão está habilitada e o status é CONCLUDED, mostrar modal de pagamento
        if (hasCompanyPermission(CompanyPermission.REQUIRE_PAYMENT_CONFIRMATION_FOR_AUTOMOTIVE_SERVICE) && status === 'CONCLUDED') {
            setServiceToUpdate(sortedServices[index]);
            setPendingStatusChange(status);
            setShowPaymentModal(true);
            return;
        }
        
        // Comportamento normal para outros status
        const response = await updateAutomotiveServiceStatus(sortedServices[index].id, status);
        if (response !== undefined) {
            setSortedServices((prevItems) =>
                prevItems.map((item, i) => {
                        return i === index ? {
                            ...item,
                            status: status,
                        } : item;
                    }
                )
            );
        } else {
            showAlert('O status não foi atualizado em nenhum serviço.');
        }
    }

    // Função para lidar com a confirmação do pagamento
    const handlePaymentConfirm = async (paymentMethodValues) => {
        try {
            setLoading(true);
            
            // Preparar dados dos pagamentos
            const paymentData = paymentMethodValues.map(payment => ({
                paymentMethod: payment.paymentMethod,
                value: payment.value,
                dueDate: payment.dueDate,
                paid: payment.paid,
                splits: payment.splits || 0
            }));

            // Atualizar apenas os pagamentos usando o novo endpoint
            const url = `${apiUrl}/api/automotiveservices/${serviceToUpdate.id}/payments`;
            const response = await makeRequest(url, 'PUT', {
                'Content-Type': 'application/json',
            }, JSON.stringify(paymentData));

            if (response) {
                // Se há um status pendente, atualizar o status do serviço
                if (pendingStatusChange) {
                    const statusResponse = await updateAutomotiveServiceStatus(serviceToUpdate.id, pendingStatusChange);
                    if (statusResponse !== undefined) {
                        // Atualizar a lista local
                        setSortedServices((prevItems) =>
                            prevItems.map((item) => {
                                if (item.id === serviceToUpdate.id) {
                                    return {
                                        ...item,
                                        status: pendingStatusChange,
                                        paymentMethods: paymentData
                                    };
                                }
                                return item;
                            })
                        );
                    }
                } else {
                    // Se não há status pendente, apenas atualizar os pagamentos
                    setSortedServices((prevItems) =>
                        prevItems.map((item) => {
                            if (item.id === serviceToUpdate.id) {
                                return {
                                    ...item,
                                    paymentMethods: paymentData
                                };
                            }
                            return item;
                        })
                    );
                    // Recarregar a lista para garantir que os dados estão atualizados
                    await makeQuery();
                }
            }
        } catch (error) {
            console.error('Error updating payments:', error);
            showAlert('Erro ao atualizar pagamentos. Tente novamente.');
        } finally {
            setLoading(false);
            setShowPaymentModal(false);
            setServiceToUpdate(null);
            setPendingStatusChange(null);
        }
    };

    // Função para fechar o modal de pagamento
    const handlePaymentClose = () => {
        setShowPaymentModal(false);
        setServiceToUpdate(null);
        setPendingStatusChange(null);
    };

    const openServiceViewer = (serviceId) => {
        setSelectedServiceId(serviceId);
        setShowServiceViewer(true);
    };

    const closeServiceViewer = () => {
        setShowServiceViewer(false);
        setSelectedServiceId(null);
    };

    async function printServiceReport(service) {
        const url = `${apiUrl}/api/automotiveservices/${service.id}/report`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    ...getAuthHeader(),
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
            } else {
                showAlert('Erro ao gerar relatório');
            }
        } catch (error) {
            console.error('Erro ao gerar relatório:', error);
            showAlert('Erro ao gerar relatório');
        }
    }

    const buildAutomotiveServiceRowActions = (service, index) => {
        const actions = [];

        if (service.customerPhone) {
            actions.push({
                key: 'whatsapp',
                label: 'WhatsApp',
                phoneNumber: service.customerPhone,
                personName: service.customerName,
                message: buildDefaultWhatsAppMessage(service.customerName),
                render: () => (
                    <WhatsAppButton
                        phoneNumber={service.customerPhone}
                        personName={service.customerName}
                    />
                ),
            });
        }

        actions.push({
            key: 'view',
            label: 'Visualizar',
            onClick: () => openServiceViewer(service.id),
        });

        actions.push({
            key: 'payments',
            label: 'Pagamentos',
            onClick: () => {
                setServiceToUpdate(service);
                setShowPaymentModal(true);
            },
        });

        actions.push({
            key: 'print',
            label: 'Imprimir',
            onClick: () => printServiceReport(service),
        });

        if (hasUserPermission(UserPermission.EDIT_AUTOMOTIVE_SERVICE)) {
            actions.push({
                key: 'edit',
                label: 'Editar',
                type: 'edit',
                onClick: () => navigate('/app/automotiveservice?id=' + service.id),
            });
        }

        if (hasUserPermission(UserPermission.REMOVE_AUTOMOTIVE_SERVICE)) {
            actions.push({
                key: 'delete',
                label: 'Excluir',
                type: 'delete',
                onClick: async () => {
                    if (await showConfirm('Deseja realmente excluir?')) {
                        await removeService(service.id);
                    }
                },
                buttonProps: {className: 'remove-button'},
            });
        }

        return actions;
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (!hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            {serviceShowPrintDialogIndex >= 0 && sortedServices[serviceShowPrintDialogIndex] && (
                <ModalDiv closeModal={() => setServiceShowPrintDialogIndex(undefined)} style_={{width: '400px'}}>
                    <div ref={componentRef}>
                        <div style={{marginLeft: '5mm'}}>
                            {(() => {
                                const s = sortedServices[serviceShowPrintDialogIndex];
                                const saleDate = s.saleDate ? formatDateToString(new Date(s.saleDate)) : '—';
                                const deliveryDate = s.deliveryDate ? formatDateTimeToString(new Date(s.deliveryDate)) : '—';
                                return (
                                    <>
                            <div
                                style={{marginBottom: '5px'}}>CÓDIGO: {s.code}</div>
                            <div
                                style={{marginBottom: '5px'}}>CLIENTE: {s.customerName}</div>
                            <div
                                style={{marginBottom: '5px'}}>CONTATO: {s.customerPhone}</div>
                            <div
                                style={{marginBottom: '5px'}}>CARRO: {s.car}</div>
                            <div
                                style={{marginBottom: '5px'}}>MARCA: {s.carBrand}</div>
                            <div
                                style={{marginBottom: '5px'}}>PLACA: {s.licensePlate}</div>
                            <div
                                style={{marginBottom: '5px'}}>MILEAGE: {s.mileage}</div>
                            <div
                                style={{marginBottom: '5px'}}>YEAR: {s.modelYear}</div>
                            <div
                                style={{marginBottom: '5px'}}>SERVIÇO: {saleDate}</div>
                            <div
                                style={{marginBottom: '5px'}}>ENTREGA: {deliveryDate}</div>
                            <div style={{marginBottom: '5px'}}>VALOR:
                                R$ {formatMoneyBr(s.totalValue)}</div>
                                    </>
                                );
                            })()}
                            
                            {/* Items */}
                            {sortedServices[serviceShowPrintDialogIndex].items && sortedServices[serviceShowPrintDialogIndex].items.length > 0 && (
                                <>
                                    <div style={{marginBottom: '10px', marginTop: '10px', fontWeight: 'bold'}}>ITENS:</div>
                                    {sortedServices[serviceShowPrintDialogIndex].items.map((item, index) => (
                                        <div key={index} style={{marginBottom: '5px', marginLeft: '10px'}}>
                                            <div>• {item.quantity}x [{item.type}] {item.product ? item.product.name : 'Produto não encontrado'} - R$ {formatMoneyBr(item.value)}</div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </ModalDiv>
            )}

            {/* Modal de confirmação de pagamento */}
            {showPaymentModal && serviceToUpdate && (
                <PaymentMethodValueSelect
                    onClose={handlePaymentClose}
                    onConfirm={handlePaymentConfirm}
                    totalValue={serviceToUpdate.totalValue}
                    paymentMethods={serviceToUpdate.paymentMethods || []}
                    futureDueDate={serviceToUpdate.deliveryDate ? new Date(serviceToUpdate.deliveryDate) : new Date()}
                    allowChangeValue={true}
                    allowChangeDueDate={true}
                />
            )}

            <ListPageLayout
                tableWidth={900}
                header={
                    <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                        <div className="header-label">Consulta de Serviços Automotivos</div>
                        {hasUserPermission(UserPermission.CREATE_AUTOMOTIVE_SERVICE) &&
                            <NewButton className={"new-button"} onClick={() => navigate('/app/automotiveservice')}/>
                        }
                    </div>
                }
                filters={
                    <div className="list-page-filters-automotive">
                <div className="list-page-filters-automotive-fields">
                    <div className="list-page-filters-field list-page-filters-query">
                        <div className="list-page-filters-automotive-label">Código/Cliente/Carro</div>
                        <input
                            type="text"
                            className="list-page-filters-query-input"
                            value={searchTerm}
                            onChange={handleInputChange}
                            data-test="search-input"
                        />
                    </div>
                    <div className="list-page-filters-field">
                        <div className="list-page-filters-automotive-label">Data de Entrega</div>
                        <div className="list-page-filters-period">
                            <DatePicker
                                className="datepicker"
                                selected={startDate}
                                onChange={handleStartDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            <span className="list-page-filters-period-separator">até</span>
                            <DatePicker
                                className="datepicker"
                                selected={endDate}
                                onChange={handleEndDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="list-page-filters-field">
                        <div className="list-page-filters-automotive-label">Status</div>
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option key="ALL" value="ALL">
                                {statusTranslate.ALL.label}
                            </option>
                            <option key="IN_PROGRESS" value="IN_PROGRESS">
                                Em Andamento
                            </option>
                            {renderAutomotiveServiceOptions(false)}
                        </select>
                    </div>

                    <div className="list-page-filters-automotive-query">
                        <SearchButton title="Consultar" onClick={handleQuery}/>
                    </div>

                </div>
            </div>
                }
                footer={hasUserPermission(UserPermission.SHOW_TOTAL_AUTOMOTIVE_SERVICE_LIST) ? (
                    <>
                        <div className="total-label">Quantidade: {services.length}</div>
                        <div className="total-label">Valor Total: R$ {formatMoneyBr(totalValue)}</div>
                    </>
                ) : null}
            >
            <div className="list-page-automotive-services">
            <table>
                <thead>
                <tr>
                    <th className="automotive-col--optional" style={{textAlign: 'center', cursor: "pointer", minWidth: "70px"}}
                        onClick={() => handleSort('code')}>
                        {getSortIcon('code')} Código
                    </th>
                    <th className="automotive-col--essential automotive-col--customer" style={{cursor: "pointer", minWidth: "100px"}} onClick={() => handleSort('customerName')}>
                        {getSortIcon('customerName')} Cliente
                    </th>
                    <th className="automotive-col--essential automotive-col--car" style={{cursor: "pointer", minWidth: "100px"}} onClick={() => handleSort('car')}>
                        {getSortIcon('car')} Carro
                    </th>
                    <th className="automotive-col--essential automotive-col--delivery" style={{textAlign: 'center', cursor: "pointer", minWidth: "100px"}}
                        onClick={() => handleSort('deliveryDate')}>
                        {getSortIcon('deliveryDate')} Data Entrega
                    </th>
                    <th className="automotive-col--essential automotive-col--value" style={{cursor: "pointer", minWidth: "70px", textAlign: 'right'}}
                        onClick={() => handleSort('totalValue')}>
                        {getSortIcon('totalValue')} Valor
                    </th>
                    <th className="automotive-col--essential automotive-col--status" style={{textAlign: 'center', cursor: "pointer", minWidth: "100px"}}
                        onClick={() => handleSort('status')}>
                        {getSortIcon('status')} Status
                    </th>
                    <th className="automotive-col--actions"/>
                </tr>
                </thead>
                <tbody style={{overflowY: 'auto'}}>
                {sortedServices.map((service, index) => {
                    const st = statusStyle(service.status);
                    return (
                    <tr key={service.id}>
                        <td className="automotive-col--optional">
                            <div style={{textAlign: 'center'}}>{service.code}</div>
                        </td>
                        <td className="automotive-col--essential automotive-col--customer">
                            <div>{service.customerName}</div>
                        </td>
                        <td className="automotive-col--essential automotive-col--car">
                            <div>{service.car}</div>
                        </td>
                        <td className="automotive-col--essential automotive-col--delivery">
                            <div style={{textAlign: 'center'}}>
                                {service.deliveryDate ? formatDateTimeNoSecondsToString(new Date(service.deliveryDate)) : '—'}
                            </div>
                        </td>
                        <td className="automotive-col--essential automotive-col--value">
                            <div style={{textAlign: 'right'}}>{formatMoneyBr(service.totalValue)}</div>
                        </td>
                        <td className="automotive-col--essential automotive-col--status">
                            <div style={{
                                padding: '3px',
                                backgroundColor: st.color,
                                borderRadius: '3px',
                                borderColor: st.color,
                                justifyContent: 'center'
                            }}>
                                <select
                                    style={{
                                        padding: '2px',
                                        backgroundColor: st.color,
                                        borderColor: st.color
                                    }}
                                    value={service.status} onChange={async (e) => {
                                        // Captura o novo status de forma síncrona: o <select> é controlado e o
                                        // showConfirm re-renderiza a lista durante o await, resetando e.target.value
                                        // de volta para o status atual antes de handleStatus ler o valor.
                                        const newStatus = e.target.value;
                                        if (!await showConfirm(`Deseja realmente mudar o status do serviço ${service.code} de '${st.label}' para '${statusStyle(newStatus).label}'?`)) {
                                            return;
                                        }
                                        handleStatus(index, newStatus)
                                    }}>
                                    {renderAutomotiveServiceOptions(false)}
                                </select>
                            </div>
                        </td>
                        <td className="automotive-col--actions table-actions">
                            <TableRowActions actions={buildAutomotiveServiceRowActions(service, index)}/>
                        </td>
                    </tr>
                    );
                })}
                </tbody>
                {services.length === 0 && (
                    <tfoot>
                    <tr>
                        <td colSpan={7}>{loading ? 'Carregando...' : 'Nenhum item encontrado.'}</td>
                    </tr>
                    </tfoot>
                )}
            </table>
            </div>
            </ListPageLayout>
            {showServiceViewer && selectedServiceId && (
                <ModalDiv closeModal={closeServiceViewer}>
                    <AutomotiveServiceViewer
                        serviceId={selectedServiceId}
                        onClose={closeServiceViewer}
                    />
                </ModalDiv>
            )}
        </>
    );
};

export default AutomotiveServiceList; 